import { useCallback, useEffect, useRef, useState } from "react";
import { type StreamNotice, type StreamStatus, api } from "../../lib/api";

// A boolean could not tell "we have not heard from the server yet" from "we
// had it and lost it" — the field this replaced, `isConnected`, meant the
// first on cold start and was RENDERED as the second everywhere it was read
// (a "Health feed reconnecting…" warning), so every page load flashed a
// warning about a connection that was never actually lost.
//
//  - "connecting": no snapshot has ever arrived. Render nothing — a first
//    connect that resolves in 400ms must not flash a warning, and neither
//    should one that's merely a little slow.
//  - "live": the feed is delivering frames normally.
//  - "interrupted": either a connection that had been live has dropped, or
//    an initial connect has gone unresolved long enough (or errored enough
//    times) that "still connecting" has stopped being the honest read.
export type FeedPhase = "connecting" | "live" | "interrupted";

// How long an initial connection may stay unresolved (no frame, no error,
// nothing) before "connecting" stops being an honest description of it. Long
// enough that an ordinary handshake never trips it, short enough that a
// genuinely stuck feed does not read as fine indefinitely.
const CONNECTING_GRACE_MS = 5_000;

// The backend pushes a frame every 2s with a 15s heartbeat — if the loop
// wedges while the underlying TCP connection stays open, EventSource's
// onerror never fires at all, so the state above would otherwise assert
// stale data forever. Five missed frames is well past normal jitter.
const STALE_FRAME_MS = 10_000;
const STALE_CHECK_INTERVAL_MS = 2_000;

export interface HealthState {
  /** Keyed by camera_id for O(1) lookup from a tile. */
  byCamera: Map<string, StreamStatus>;
  phase: FeedPhase;
}

export interface HealthFeed extends HealthState {
  /** Subscribe to every notice this session receives (eviction, request
   *  answered). Returns the unsubscribe function.
   *
   * Notices are DELIVERED, never stored — see the comment on the old
   * `lastEviction`/`lastAnswer` fields this replaced, in git history. They
   * used to be parked in this same state object, and every health frame —
   * one every two seconds — replaced the whole object and dropped whichever
   * notice hadn't been consumed yet. Spreading `prev` there would have fixed
   * only that one cause of loss and left two worse ones: two notices parsed
   * out of a single SSE chunk are dispatched inside the same task, so React
   * batches both `setState` calls and the second silently overwrites the
   * first — and two approvals answered back to back is an ordinary event,
   * not a corner case. And a value that is never cleared gets REPLAYED into
   * every consumer that subscribes afterwards — a tile that mounts a minute
   * later would see an eviction that happened a minute ago as if it just
   * arrived. An event has no lifetime to get wrong: it reaches whoever is
   * listening at the moment it arrives, and is then gone. */
  subscribeNotice: (listener: (notice: StreamNotice) => void) => () => void;
}

// Subscribes to the backend's SSE health channel and keeps a live map of
// per-camera stream status. One connection shared by the whole app (mounted
// once in App), rather than one per tile — nine tiles opening nine SSE
// connections to poll the same backend loop would be wasteful and is exactly
// what the mandate's "no in-memory per-connection sprawl" reasoning warns
// against on the server side; the same logic applies to the client.
export function useHealthEvents(): HealthFeed {
  const [state, setState] = useState<HealthState>({ byCamera: new Map(), phase: "connecting" });
  const retryDelayRef = useRef(1_000);

  // Plain closure store, not React state — same reasoning as streamSlots.ts
  // and tileCommands.ts: delivering a notice must not force every subscriber
  // to re-render (most notices are irrelevant to most tiles), and a listener
  // set survives independently of whatever the health snapshot is doing.
  const listenersRef = useRef(new Set<(notice: StreamNotice) => void>());

  const subscribeNotice = useCallback((listener: (notice: StreamNotice) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    let source: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    // Plain closure variables, not refs: nothing outside this effect
    // instance reads them, so there's no reason to survive a re-render
    // (this effect never re-runs — its deps are `[]`) or be visible
    // elsewhere.
    let hasEverConnected = false;
    let lastFrameAt = 0;

    const markInterrupted = () => {
      setState((prev) => ({ ...prev, phase: "interrupted" }));
    };

    const connect = () => {
      if (stopped) return;
      // withCredentials: the health feed now sits behind requireAuth like
      // every other route — without this, the browser omits the session
      // cookie on this cross-origin request and every connection attempt
      // gets a 401.
      source = new EventSource(api.healthEventsUrl(), { withCredentials: true });

      source.addEventListener("health", (evt) => {
        retryDelayRef.current = 1_000; // reset backoff on any successful message
        try {
          const statuses = JSON.parse((evt as MessageEvent).data) as StreamStatus[];
          const byCamera = new Map(statuses.map((s) => [s.camera_id, s]));
          hasEverConnected = true;
          lastFrameAt = Date.now();
          // A plain whole-object replace, and correctly so: everything left
          // in this state is a SNAPSHOT — every field is "what is true
          // right now" — since notices (which are not snapshots, but
          // one-shot facts) moved to the subscription above.
          setState({ byCamera, phase: "live" });
        } catch {
          // A malformed frame must not take the whole subscription down.
        }
      });

      // Its own event type, not folded into "health": these are messages an
      // operator must not miss, and parsing them out of a status payload that
      // arrives every two seconds would mean a dropped frame is a lost
      // message rather than a stale number.
      source.addEventListener("notice", (evt) => {
        let notice: StreamNotice;
        try {
          notice = JSON.parse((evt as MessageEvent).data) as StreamNotice;
        } catch {
          // A malformed notice must not take the subscription down.
          return;
        }
        // Snapshotted before delivery: a listener may unsubscribe while
        // being notified (a tile this very command stops).
        for (const listener of [...listenersRef.current]) {
          // One throwing subscriber must not stop the notice reaching the
          // rest — same reasoning as the parse guard above, one level up.
          try {
            listener(notice);
          } catch {
            // A broken subscriber's problem, not this channel's.
          }
        }
      });

      source.onerror = () => {
        source?.close();
        // A connection that had never succeeded stays "connecting" here —
        // indistinguishable, from the outside, from one that is merely
        // slow. CONNECTING_GRACE_MS below is what eventually calls that.
        // Once it HAS succeeded at least once, an error is a real, visible
        // interruption.
        if (hasEverConnected) markInterrupted();
        if (stopped) return;
        // EventSource retries on its own, but its built-in retry is fixed and
        // can hammer a backend that's actually down. Backing off here is
        // cheap insurance; the browser's native retry still fires in parallel
        // on a genuinely brief blip, which is fine — connect() is idempotent
        // enough that an extra attempt just replaces the source.
        retryTimer = setTimeout(connect, retryDelayRef.current);
        retryDelayRef.current = Math.min(retryDelayRef.current * 2, 30_000);
      };
    };

    connect();

    const connectingTimer = setTimeout(() => {
      if (!hasEverConnected) markInterrupted();
    }, CONNECTING_GRACE_MS);

    // Catches the wedged-loop case onerror cannot: the TCP connection stays
    // open, nothing ever calls onerror, and byCamera would otherwise assert
    // ever-staler data forever with no signal that anything is wrong.
    const staleTimer = setInterval(() => {
      if (hasEverConnected && Date.now() - lastFrameAt > STALE_FRAME_MS) markInterrupted();
    }, STALE_CHECK_INTERVAL_MS);

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      clearTimeout(connectingTimer);
      clearInterval(staleTimer);
      source?.close();
    };
  }, []);

  return { ...state, subscribeNotice };
}
